import { CSSResult, html, LitElement, nothing, PropertyValues, TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { RecoProductBasedRecommendationsItemData } from '@cube/tracking-library-api';
import { trackRecoClick, trackRecoView } from '../../../scripts/service/cube-tracking-layer.js';
import { Price, Product } from '../../../@types/wishlist.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import styles from './cart-favorites.scss?inline';
import arrowLeft from '@breuni-ui/design-tokens/images/icons/o-ic-16_chevron-left.svg?element';
import arrowRight from '@breuni-ui/design-tokens/images/icons/o-ic-16_chevron-right.svg?element';
import saleIcon from '@breuni-ui/design-tokens/images/icons/o-ic-12_sale.svg?element';
import { buildPdsUrl } from '../../../scripts/service/build-pds-url.js';
import { ProductIdentifier } from '../../../scripts/service/product-identifier.js';
import '../carousel/index.js';
import '../price/price.js';
import '../wishlist-button/wishlist-button.js';
import { addTitleToIcon, hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';
import { getPictureSourceSet, productImageUrl } from '../../../scripts/service/image-formatter.js';
import { renderAiBadge } from '../ai-badge/ai-badge.js';
import { loadFromDOM } from '../../../scripts/utils/load-from-dom.js';
import { cartFavoritesApi } from '../../../scripts/service/cart-favorites-api.js';
import { cartApi } from '../../../scripts/service/cart-api.js';
import { CartFavoritesTracking, defaultTracking, trackingWithDefaults } from './cart-favorites-tracking.js';
import { disconnectObserver } from './cart-favorites-utils.js';

@customElement('ents-cart-favorites')
export class CartFavorites extends LitElement {
  private static readonly IMAGE_SIZES =
    '(min-width: 1920px) 342px, (min-width: 1660px) 20vw, (min-width: 1160px) 25vw, ' +
    '(min-width: 760px) 33vw, (min-width: 560px) 50vw, 153px';

  @property({ type: String, attribute: 'data-endpoint' })
  private endpoint: string = '';

  @property({ type: String, attribute: 'data-cart-endpoint' })
  private cartEndpoint: string = '';

  @property({ type: String, attribute: 'data-image-host' })
  private imageHost: string = '';

  @property({ type: String, attribute: 'data-headline' })
  private headline: string = '';

  @property({ type: String, attribute: 'data-message-slider-previous' })
  private sliderPreviousMessage: string = '';

  @property({ type: String, attribute: 'data-message-slider-next' })
  private sliderNextMessage: string = '';

  @property({ type: String, attribute: 'data-message-on-sale' })
  private onSaleMessage: string = '';

  @property({ type: String, attribute: 'data-message-price-current-label' })
  private priceCurrentLabelMessage: string = '';

  @property({ type: String, attribute: 'data-message-price-unit-label' })
  private priceUnitLabelMessage: string = '';

  @property({ type: String, attribute: 'data-message-price-lowest-label' })
  private priceLowestLabelMessage: string = '';

  @property({ type: String, attribute: 'data-message-price-lowest-short-label' })
  private priceLowestShortLabelMessage: string = '';

  @property({ type: String, attribute: 'data-message-price-original-label' })
  private priceOriginalLabelMessage: string = '';

  @property({ type: String, attribute: 'data-message-price-original-short-label' })
  private priceOriginalShortLabelMessage: string = '';

  @state()
  private items: Product[] = [];

  @state()
  private itemsReady: boolean = false;

  @query('[data-ents-cart-favorites-tracker]')
  private trackerElement?: HTMLElement;

  private tracking: CartFavoritesTracking = defaultTracking();

  private trackingItems: RecoProductBasedRecommendationsItemData[] = [];

  private isVisible: boolean = false;

  private viewSent: boolean = false;

  private observer?: IntersectionObserver;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.loadTracking();
    this.observeVisibility();
    this.loadItems();
  }

  disconnectedCallback() {
    disconnectObserver(this.observer);
    super.disconnectedCallback();
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    if (changedProperties.has('items') && this.items.length > 0) {
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('camp:aktionsflags:initialized'));
      }, 0);
    }
  }

  protected render(): TemplateResult {
    const shouldRenderItems = this.items.length > 0;

    return html`
      ${
        shouldRenderItems
          ? html`
              <ents-carousel-container
                carousel-selector="ents-carousel"
                navigation-next-selector="ents-carousel-navi-next"
                navigation-prev-selector="ents-carousel-navi-prev"
                class="ents-cart-favorites"
                data-ents-cart-favorites-slide-count="${this.items.length}"
                data-test-ents-cart-favorites
              >
                <header slot="header">
                  <div class="container">
                    <div class="container-center carousel-header">
                      <div class="carousel-header--heading">
                        <h2 class="ents-heading" data-test-ents-cart-favorites-headline>${this.headline}</h2>
                      </div>
                      <div class="carousel-header--navigation">
                        <ents-carousel-navi-prev>
                          <button class="bdk-button-icon" size="extra-small" variant="outline">
                            ${hideIconFromAssistiveTechnology(arrowLeft())}
                            <span class="sr-only">${this.sliderPreviousMessage}</span>
                          </button>
                        </ents-carousel-navi-prev>
                        <ents-carousel-navi-next>
                          <button class="bdk-button-icon" size="extra-small" variant="outline">
                            ${hideIconFromAssistiveTechnology(arrowRight())}
                            <span class="sr-only">${this.sliderNextMessage}</span>
                          </button>
                        </ents-carousel-navi-next>
                      </div>
                    </div>
                  </div>
                </header>
                <div slot="body">
                  <ents-carousel class="ents-carousel ents-cart-favorites__carousel" spacing="2" no-aria-live-polite>
                    ${this.items.map((item, index) => html` <article>${this.renderItem(item, index)}</article> `)}
                  </ents-carousel>
                </div>
              </ents-carousel-container>
            `
          : nothing
      }
      <span data-ents-cart-favorites-tracker aria-hidden="true"></span>
    `;
  }

  private renderItem(item: Product, index: number): TemplateResult {
    const price = item.price;
    const title = `${item.brandName} ${item.name ?? ''}`.trim();
    const pdsUrl = this.getPdsUrl(item);
    const initialWishlistState = ProductIdentifier.create(item.syan, item.colorId).id;
    const imageUrl = item.defaultImageUrls?.[0]?.url;
    const imageFallback = imageUrl ? productImageUrl('287x393_retina', imageUrl, 'jpg', this.imageHost) : undefined;
    const fallbackImage = imageFallback
      ? html` <img loading="lazy" src="${imageFallback}" alt="${title}" class="ents-cart-favorites-product__image" /> `
      : nothing;
    const trackingData = JSON.stringify({
      syan: item.syan,
      farb_id: item.colorId,
      artikel_id: item.articleId,
      is_sponsored: false,
      target_url: pdsUrl,
    });

    return html`
      <div
        class="ents-cart-favorites-product ents-cart-favorites__product"
        data-position="${index + 1}"
        data-tracking-data="${trackingData}"
        data-ents-cart-favorites-item
      >
        <div class="ents-cart-favorites-product__inner">
          <a
            href="${pdsUrl}"
            class="ents-cart-favorites-product__link"
            title="${title}"
            @click="${() => this.onItemClick(item, index)}"
          >
            <div class="ents-cart-favorites-product__image-container">
              ${
                imageUrl
                  ? html`
                      <picture aria-hidden="true">
                        <source
                          type="image/webp"
                          srcset="${getPictureSourceSet(imageUrl, 'webp', this.imageHost)}"
                          sizes="${CartFavorites.IMAGE_SIZES}"
                        />
                        ${fallbackImage}
                      </picture>
                    `
                  : nothing
              }
              ${
                item.defaultImageUrls?.[0]?.aiGenerated
                  ? renderAiBadge('ents-cart-favorites-product__ai-badge')
                  : nothing
              }
            </div>
            ${price?.showSaleFlag ? this.renderSaleFlag(price.blackWeek) : nothing}
            <camp-aktionsflag
              syan="${item.syan}"
              farb-id="${ifDefined(item.colorId)}"
              is-reco-slider
            ></camp-aktionsflag>
            <div class="ents-cart-favorites-product__details">
              <div class="ents-cart-favorites-product__brand ents-copy-small-bold ents-s:copy-bold">
                ${item.brandName}
              </div>
              <div class="ents-cart-favorites-product__name-container">
                <div class="ents-cart-favorites-product__name ents-copy-small ents-s:copy">${item.name}</div>
                ${
                  item.subName
                    ? html`
                        <div class="ents-cart-favorites-product__sub-name ents-copy-small ents-s:copy">
                          ${item.subName}
                        </div>
                      `
                    : nothing
                }
              </div>
              ${price ? this.renderPrice(price) : nothing}
            </div>
          </a>
          <div class="ents-cart-favorites-product__wishlist-button">
            <ents-wishlist-button
              initial-state='["${initialWishlistState}"]'
              placement="cart-favorites"
              syan="${item.syan}"
              color-id="${ifDefined(item.colorId)}"
              article-id="${ifDefined(item.articleId)}"
              data-test-ents-cart-favorites-wishlist-button
            ></ents-wishlist-button>
          </div>
        </div>
      </div>
    `;
  }

  private renderSaleFlag(blackWeek: boolean | undefined): TemplateResult {
    return html`
      <div
        class="bdk-badge-article ents-badge-wrapper ents-badge__cart-favorites"
        variant="sale"
        size="small"
        black-week="${blackWeek ? '' : nothing}"
        data-test-ents-sale-flag
      >
        ${addTitleToIcon(saleIcon(), this.onSaleMessage)}
      </div>
    `;
  }

  private renderPrice(price: Price): TemplateResult {
    return html`
      <ents-price
        .price="${price}"
        .messagesOverride="${this.priceMessages}"
        badge-size="small"
        data-test-ents-cart-favorites-price
      ></ents-price>
    `;
  }

  private get priceMessages() {
    return {
      ents_wishlist_product_price_currentPrice_label: this.priceCurrentLabelMessage,
      ents_wishlist_product_price_unitPrice_label: this.priceUnitLabelMessage,
      ents_wishlist_product_price_lowestPrice_label: this.priceLowestLabelMessage,
      ents_wishlist_product_price_lowestPrice_shortLabel: this.priceLowestShortLabelMessage,
      ents_wishlist_product_price_originalPrice_label: this.priceOriginalLabelMessage,
      ents_wishlist_product_price_originalPrice_shortLabel: this.priceOriginalShortLabelMessage,
    };
  }

  private loadTracking() {
    const tracking = loadFromDOM('#ents-cart-favorites-tracking') as Partial<CartFavoritesTracking> | undefined;
    this.tracking = trackingWithDefaults(tracking);
  }

  private async loadItems() {
    if (!this.endpoint) {
      this.itemsReady = true;
      this.maybeSendView();
      return;
    }

    try {
      const excludeProductIds = await this.loadCartProductIds();
      const data = await cartFavoritesApi.getItems(this.endpoint, excludeProductIds);
      const items = data?.items ?? [];
      this.items = items;
      this.trackingItems = this.buildTrackingItems(items);
    } catch {
      // ignored — slider stays empty
    } finally {
      this.itemsReady = true;
      this.maybeSendView();
    }
  }

  private async loadCartProductIds(): Promise<string[]> {
    if (!this.cartEndpoint) {
      return [];
    }
    try {
      const cartPositions = await cartApi.getCartProductIds(this.cartEndpoint);
      return cartPositions.positionen.map((p) => ProductIdentifier.create(p.syan, p.farbId).id);
    } catch {
      // Cart fetch failures must not block slider rendering — fall back to no filtering.
      return [];
    }
  }

  private buildTrackingItems(items: Product[]): RecoProductBasedRecommendationsItemData[] {
    return items.map((item, index) => ({
      syan: `${item.syan}`,
      farb_id: item.colorId,
      artikel_id: item.articleId,
      is_sponsored: false,
      target_url: this.getPdsUrl(item),
      position: index + 1,
    })) as RecoProductBasedRecommendationsItemData[];
  }

  private onItemClick(item: Product, index: number) {
    const pdsUrl = this.getPdsUrl(item);
    const itemTracking = this.trackingItems[index] ?? {
      syan: `${item.syan}`,
      farb_id: item.colorId,
      artikel_id: item.articleId,
      is_sponsored: false,
      target_url: pdsUrl,
      position: index + 1,
    };
    trackRecoClick({
      promotion_data: this.getPromotionData(),
      event_data: {
        is_interaction_event: true,
      },
      items: [itemTracking],
      currency: { code: this.tracking.currency },
    });
  }

  private observeVisibility() {
    if (!this.trackerElement) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      this.isVisible = true;
      this.maybeSendView();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.isVisible = true;
            this.maybeSendView();
          }
        });
      },
      { threshold: [0.1] },
    );

    this.observer.observe(this.trackerElement);
  }

  private maybeSendView() {
    if (this.viewSent || !this.itemsReady || !this.isVisible) {
      return;
    }
    this.viewSent = true;
    trackRecoView({
      promotion_data: this.getPromotionData(),
      event_data: {
        is_interaction_event: false,
      },
      items: this.trackingItems,
      currency: { code: this.tracking.currency },
    });
  }

  private getPromotionData() {
    return {
      type: 'slider',
      name: this.tracking.name ?? '',
      placement: 'cart',
      rule: this.tracking.rule ?? '',
    };
  }

  private getPdsUrl(item: Product): string {
    return buildPdsUrl(item.pdsUrlBasePath, item.articleId, item.colorId) ?? item.pdsUrlBasePath ?? '';
  }
}
